/**
 * prefabs.ts — reusable build prefabs (Edit Mode: "Save selection as prefab").
 *
 * A prefab is a set of freeform parts stored RELATIVE to a pivot (the
 * selection's footprint center: centroid in X/Z, lowest point in Y) so it can
 * be placed anywhere at any yaw. Pure math over part snapshots — the Edit Mode
 * module turns `instantiatePrefab` into one GroupCmd of RezCmds (one undo step)
 * and keeps the library in localStorage per device (the asset pipeline's
 * cross-device sharing is a later wave).
 */
import type { PartRowSnapshot } from './build_commands.js';

export interface PrefabPart {
  primType: string;
  prefabKey: string;
  dx: number; dy: number; dz: number;
  rotX: number; rotY: number; rotZ: number; rotW: number;
  sx: number; sy: number; sz: number;
  shapeJson: string;
  materialJson: string;
  physics: boolean;
}

export interface PrefabDef {
  name: string;
  parts: PrefabPart[];
  /** Footprint size (m) for the placement ghost. */
  size: { x: number; y: number; z: number };
  createdAt: number;
}

export const PREFAB_STORE_KEY = 'atlas:prefabs';
export const MAX_PREFABS = 40;
export const MAX_PREFAB_PARTS = 120;

/** Capture parts relative to their footprint pivot (centroid X/Z, min Y). */
export function capturePrefab(name: string, snaps: PartRowSnapshot[], now = Date.now()): PrefabDef {
  const list = snaps.slice(0, MAX_PREFAB_PARTS);
  if (list.length === 0) throw new Error('Select at least one part');
  let cx = 0, cz = 0, minY = Infinity;
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxY = -Infinity;
  for (const s of list) {
    cx += s.posX; cz += s.posZ;
    const hx = Math.abs(s.scaleX) / 2, hy = Math.abs(s.scaleY) / 2, hz = Math.abs(s.scaleZ) / 2;
    minY = Math.min(minY, s.posY - hy); maxY = Math.max(maxY, s.posY + hy);
    minX = Math.min(minX, s.posX - hx); maxX = Math.max(maxX, s.posX + hx);
    minZ = Math.min(minZ, s.posZ - hz); maxZ = Math.max(maxZ, s.posZ + hz);
  }
  cx /= list.length; cz /= list.length;
  return {
    name: name.trim().slice(0, 40) || 'Prefab',
    parts: list.map((s) => ({
      primType: s.primType, prefabKey: s.prefabKey,
      dx: s.posX - cx, dy: s.posY - minY, dz: s.posZ - cz,
      rotX: s.rotX, rotY: s.rotY, rotZ: s.rotZ, rotW: s.rotW,
      sx: s.scaleX, sy: s.scaleY, sz: s.scaleZ,
      shapeJson: s.shapeJson, materialJson: s.materialJson, physics: s.physics,
    })),
    size: { x: Math.max(0.1, maxX - minX), y: Math.max(0.1, maxY - minY), z: Math.max(0.1, maxZ - minZ) },
    createdAt: now,
  };
}

/** Quaternion product a ⊗ b (apply b, then a). */
function qmul(a: [number, number, number, number], b: [number, number, number, number]): [number, number, number, number] {
  const [ax, ay, az, aw] = a, [bx, by, bz, bw] = b;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

export interface RezSpec { catalog: boolean; args: Record<string, unknown> }

/** The rez argument sets for placing `def` with its pivot at `at`, turned by `yaw` (radians, three.js Y). */
export function instantiatePrefab(def: PrefabDef, worldId: bigint, at: { x: number; y: number; z: number }, yaw: number): RezSpec[] {
  const c = Math.cos(yaw), s = Math.sin(yaw);
  const qy: [number, number, number, number] = [0, Math.sin(yaw / 2), 0, Math.cos(yaw / 2)];
  return def.parts.map((p) => {
    // three.js Y rotation of an offset: x' = x·cos + z·sin, z' = −x·sin + z·cos
    const x = at.x + (p.dx * c + p.dz * s);
    const z = at.z + (-p.dx * s + p.dz * c);
    const y = at.y + p.dy;
    const [rx, ry, rz, rw] = qmul(qy, [p.rotX, p.rotY, p.rotZ, p.rotW]);
    const xf = { worldId, posX: x, posY: y, posZ: z, rotX: rx, rotY: ry, rotZ: rz, rotW: rw, scaleX: p.sx, scaleY: p.sy, scaleZ: p.sz, physics: p.physics };
    if (p.primType === 'mesh' && p.prefabKey) return { catalog: true, args: { ...xf, prefabKey: p.prefabKey } };
    return { catalog: false, args: { ...xf, primType: p.primType, shapeJson: p.shapeJson, materialJson: p.materialJson } };
  });
}

// ─── localStorage library ────────────────────────────────────────────────────

export function loadPrefabs(storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): PrefabDef[] {
  if (!storage) return [];
  try {
    const raw = JSON.parse(storage.getItem(PREFAB_STORE_KEY) ?? '[]') as unknown;
    return Array.isArray(raw) ? (raw as PrefabDef[]).filter((p) => p && typeof p.name === 'string' && Array.isArray(p.parts)) : [];
  } catch { return []; }
}

export function savePrefabs(list: PrefabDef[], storage: Pick<Storage, 'getItem' | 'setItem'> | null = typeof localStorage === 'undefined' ? null : localStorage): boolean {
  if (!storage) return false;
  try { storage.setItem(PREFAB_STORE_KEY, JSON.stringify(list.slice(-MAX_PREFABS))); return true; } catch { return false; }
}

/** Upsert by name (newest wins), bounded to MAX_PREFABS. */
export function upsertPrefab(list: PrefabDef[], def: PrefabDef): PrefabDef[] {
  return [...list.filter((p) => p.name !== def.name), def].slice(-MAX_PREFABS);
}
